"use client"

import React, { useEffect, useState, useMemo } from 'react'
import { Helmet } from 'react-helmet-async'
import { Link } from 'react-router-dom'
import { BreadcrumbsJsonLd } from '@/components/seo/Breadcrumbs'
import { motion, AnimatePresence } from 'framer-motion'
import { supabase } from '@/lib/supabase'
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import { Skeleton } from "@/components/ui/skeleton"
import { Search, Calendar, Clock, ArrowRight } from 'lucide-react'

interface BlogPost {
  id: string
  title: string
  slug: string
  excerpt: string
  content: string
  image_url: string
  category: string | null
  tags: string[]
  featured: boolean
  created_at: string
  reading_time_minutes: number
}

// Placeholder posts for development/empty state
const placeholderPosts: BlogPost[] = [
  {
    id: '1',
    title: 'Understanding Your Menstrual Cycle',
    slug: 'understanding-menstrual-cycle',
    excerpt: 'A deep dive into the phases of your cycle and how Chinese medicine can support hormonal balance.',
    content: '',
    image_url: 'https://images.unsplash.com/photo-1518495973542-4542c06a5843?w=800&q=80',
    category: 'Womens Health',
    tags: ['hormones', 'menstrual health'],
    featured: true,
    created_at: '2025-01-05T00:00:00Z',
    reading_time_minutes: 5
  },
  {
    id: '2',
    title: 'Acupuncture for Fertility: What to Expect',
    slug: 'acupuncture-fertility-what-to-expect',
    excerpt: 'Everything you need to know about fertility acupuncture treatments and how they can support your journey.',
    content: '',
    image_url: 'https://images.unsplash.com/photo-1544161515-4ab6ce6db874?w=800&q=80',
    category: 'Fertility',
    tags: ['fertility', 'acupuncture'],
    featured: false,
    created_at: '2025-01-03T00:00:00Z',
    reading_time_minutes: 7
  },
  {
    id: '3',
    title: 'Spring Wellness with Chinese Herbs',
    slug: 'spring-wellness-chinese-herbs',
    excerpt: 'Seasonal tips for maintaining balance and vitality using traditional herbal formulas.',
    content: '',
    image_url: 'https://images.unsplash.com/photo-1515377905703-c4788e51af15?w=800&q=80',
    category: 'Herbal Medicine',
    tags: ['herbs', 'seasonal wellness'],
    featured: false,
    created_at: '2025-01-01T00:00:00Z',
    reading_time_minutes: 4
  },
  {
    id: '4',
    title: 'Managing Pregnancy Symptoms Naturally',
    slug: 'managing-pregnancy-symptoms-naturally',
    excerpt: 'Gentle and effective approaches to common pregnancy discomforts using acupuncture and herbs.',
    content: '',
    image_url: 'https://images.unsplash.com/photo-1493894473891-10fc1e5dbd22?w=800&q=80',
    category: 'Pregnancy',
    tags: ['pregnancy', 'natural remedies'],
    featured: true,
    created_at: '2024-12-28T00:00:00Z',
    reading_time_minutes: 6
  },
  {
    id: '5',
    title: 'The Art of Rest: Sleep & Chinese Medicine',
    slug: 'art-of-rest-sleep-chinese-medicine',
    excerpt: 'Discover how traditional Chinese medicine views sleep and learn techniques for better rest.',
    content: '',
    image_url: 'https://images.unsplash.com/photo-1531353826977-0941b4779a1c?w=800&q=80',
    category: 'Lifestyle',
    tags: ['sleep', 'wellness'],
    featured: false,
    created_at: '2024-12-25T00:00:00Z',
    reading_time_minutes: 5
  },
  {
    id: '6',
    title: 'Nourishing Postpartum Recovery',
    slug: 'nourishing-postpartum-recovery',
    excerpt: 'Traditional wisdom for the fourth trimester: supporting new mothers with warmth and care.',
    content: '',
    image_url: 'https://images.unsplash.com/photo-1492725764893-90b379c2b6e7?w=800&q=80',
    category: 'Postpartum',
    tags: ['postpartum', 'motherhood'],
    featured: false,
    created_at: '2024-12-20T00:00:00Z',
    reading_time_minutes: 8
  }
]

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  })
}

function BlogCardSkeleton() {
  return (
    <div className="group relative overflow-hidden rounded-2xl bg-white shadow-sm">
      <Skeleton className="aspect-[4/5] w-full" />
      <div className="absolute bottom-0 left-0 right-0 p-6">
        <Skeleton className="h-4 w-20 mb-3" />
        <Skeleton className="h-6 w-full mb-2" />
        <Skeleton className="h-4 w-3/4" />
      </div>
    </div>
  )
}

function BlogCard({ post, index }: { post: BlogPost; index: number }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: index * 0.1, ease: [0.25, 0.46, 0.45, 0.94] }}
      className="group"
    >
      <Link 
        to={`/blog/${post.slug}`}
        className="block relative overflow-hidden rounded-2xl bg-white shadow-sm hover:shadow-xl transition-all duration-500"
      >
        {/* Image Container */}
        <div className="relative aspect-[4/5] overflow-hidden">
          <img
            src={post.image_url}
            alt={post.title}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            loading="lazy"
            decoding="async"
          />
          
          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-60 group-hover:opacity-80 transition-opacity duration-500" />
          
          {/* Featured Badge */}
          {post.featured && (
            <div className="absolute top-4 left-4">
              <Badge className="bg-[#DBB981] text-[#231f20] hover:bg-[#DBB981]/90 font-medium">
                Featured
              </Badge>
            </div>
          )}
          
          {/* Category Badge */}
          {post.category && (
            <div className="absolute top-4 right-4">
              <Link
                to={`/blog/category/${post.category.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')}`}
                onClick={(e) => e.stopPropagation()}
              >
                <Badge variant="secondary" className="bg-white/90 text-[rgb(132,137,116)] backdrop-blur-sm hover:bg-[rgb(132,137,116)] hover:text-white transition-colors cursor-pointer">
                  {post.category}
                </Badge>
              </Link>
            </div>
          )}
          
          {/* Content Overlay */}
          <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
            {/* Meta Info */}
            <div className="flex items-center gap-4 text-sm text-white/80 mb-3">
              <span className="flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5" />
                {formatDate(post.created_at)}
              </span>
              <span className="flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5" />
                {post.reading_time_minutes} min read
              </span>
            </div>
            
            {/* Title */}
            <h2 className="text-xl font-medium leading-snug mb-2 group-hover:text-[#DBB981] transition-colors duration-300">
              {post.title}
            </h2>
            
            {/* Excerpt - Hidden by default, shows on hover */}
            <p className="text-sm text-white/80 line-clamp-2 opacity-0 group-hover:opacity-100 transform translate-y-2 group-hover:translate-y-0 transition-all duration-300">
              {post.excerpt}
            </p>
            
            {/* Read More Arrow */}
            <div className="flex items-center gap-2 mt-3 text-[#DBB981] opacity-0 group-hover:opacity-100 transform translate-x-[-10px] group-hover:translate-x-0 transition-all duration-300 delay-100">
              <span className="text-sm font-medium">Read Article</span>
              <ArrowRight className="w-4 h-4" />
            </div>
          </div>
        </div>
      </Link>
    </motion.div>
  )
}

export default function BlogPage() {
  const [posts, setPosts] = useState<BlogPost[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
  const [usePlaceholders, setUsePlaceholders] = useState(false)

  useEffect(() => {
    async function fetchPosts() {
      setIsLoading(true)
      
      try {
        const { data, error } = await supabase
          .from('blog_posts')
          .select('*')
          .eq('published', true)
          .order('created_at', { ascending: false })
        
        if (error) {
          console.error('Error fetching blog posts:', error)
          // Use placeholder posts if Supabase fails
          setUsePlaceholders(true)
          setPosts(placeholderPosts)
        } else if (data && data.length > 0) {
          setPosts(data)
        } else {
          // Use placeholders if no posts exist
          setUsePlaceholders(true)
          setPosts(placeholderPosts)
        }
      } catch (err) {
        console.error('Error connecting to Supabase:', err)
        setUsePlaceholders(true)
        setPosts(placeholderPosts)
      }
      
      setIsLoading(false)
    }

    fetchPosts()
  }, [])

  // Extract unique categories
  const categories = useMemo(() => {
    const cats = new Set(posts.map(post => post.category).filter(Boolean))
    return Array.from(cats).sort()
  }, [posts])

  // Filter posts based on search and category
  const filteredPosts = useMemo(() => {
    return posts.filter(post => {
      const matchesSearch = searchQuery === '' || 
        post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        post.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (post.tags && post.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase())))
      
      const matchesCategory = selectedCategory === null || (post.category && post.category === selectedCategory)
      
      return matchesSearch && matchesCategory
    })
  }, [posts, searchQuery, selectedCategory])

  // Separate featured posts
  const featuredPosts = filteredPosts.filter(post => post.featured)
  const regularPosts = filteredPosts.filter(post => !post.featured)

  return (
    <main className="min-h-screen bg-[#FDFBF7]">
      <Helmet>
        <title>Blog | Raintree Chinese Medicine & Women's Health</title>
        <meta name="description" content="Articles on acupuncture, herbal medicine, fertility, pregnancy, and women's health from Raintree Chinese Medicine." />
        <meta property="og:title" content="Raintree Blog | Chinese Medicine & Women's Health" />
        <meta property="og:description" content="Articles on acupuncture, herbal medicine, fertility, pregnancy, and women's health from Raintree Chinese Medicine." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://raintreechinesemedicine.com.au/blog" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Raintree Blog | Chinese Medicine & Women's Health" />
        <meta name="twitter:description" content="Articles on acupuncture, herbal medicine, fertility, pregnancy, and women's health from Raintree Chinese Medicine." />
      </Helmet>
      <h1 className="sr-only">Raintree Blog</h1>
      <BreadcrumbsJsonLd />
      
      {/* Hero Section */}
      <section className="pt-32 pb-12 bg-gradient-to-b from-[rgb(132,137,116)]/5 to-transparent">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h1 className="text-5xl md:text-6xl font-light mb-6 text-[rgb(132,137,116)] tracking-tight">
              Insights & Wisdom
            </h1>
            <p className="text-lg text-gray-600 leading-relaxed mb-8 max-w-2xl mx-auto">
              Explore articles on Chinese medicine, women's health, fertility, and holistic wellbeing from Dr. Renee Knott.
            </p>
            
            {/* Search Bar */}
            <div className="relative max-w-md mx-auto">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                type="text"
                placeholder="Search articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 pr-4 py-6 text-base rounded-full border-gray-200 bg-white shadow-sm focus:ring-2 focus:ring-[rgb(132,137,116)]/20 focus:border-[rgb(132,137,116)]"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Category Pills */}
      {categories.length > 0 && (
        <section className="pb-8">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="flex flex-wrap justify-center gap-3"
            >
              <button
                onClick={() => setSelectedCategory(null)}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                  selectedCategory === null
                    ? 'bg-[rgb(132,137,116)] text-white shadow-md'
                    : 'bg-white text-gray-600 hover:bg-gray-50 shadow-sm'
                }`}
              >
                All
              </button>
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                    selectedCategory === category
                      ? 'bg-[rgb(132,137,116)] text-white shadow-md'
                      : 'bg-white text-gray-600 hover:bg-gray-50 shadow-sm'
                  }`}
                >
                  {category}
                </button>
              ))}
            </motion.div>
          </div>
        </section>
      )}

      {/* Placeholder Notice */}
      {usePlaceholders && (
        <div className="container mx-auto px-4 mb-8">
          <div className="bg-[#DBB981]/10 border border-[#DBB981]/30 rounded-xl p-4 text-center">
            <p className="text-sm text-[rgb(132,137,116)]">
              Displaying sample articles. Connect your CMS to add real blog posts.
            </p>
          </div>
        </div>
      )}

      {/* Blog Grid */}
      <section className="pb-20">
        <div className="container mx-auto px-4">
          {isLoading ? (
            // Loading Skeleton Grid
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {[...Array(6)].map((_, i) => (
                <BlogCardSkeleton key={i} />
              ))}
            </div>
          ) : filteredPosts.length === 0 ? (
            // Empty State
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-16"
            >
              <div className="text-6xl mb-4">🍃</div>
              <h3 className="text-xl text-gray-600 mb-2">No articles found</h3>
              <p className="text-gray-500">Try adjusting your search or filter criteria.</p>
            </motion.div>
          ) : (
            <AnimatePresence mode="wait">
              {/* Featured Posts Row */}
              {featuredPosts.length > 0 && (
                <div className="mb-12">
                  <h2 className="text-sm font-medium text-[rgb(132,137,116)] uppercase tracking-wider mb-6">Featured</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
                    {featuredPosts.slice(0, 2).map((post, index) => (
                      <BlogCard key={post.id} post={post} index={index} />
                    ))}
                  </div>
                </div>
              )}
              
              {/* Regular Posts Grid */}
              {regularPosts.length > 0 && (
                <div>
                  {featuredPosts.length > 0 && (
                    <h2 className="text-sm font-medium text-[rgb(132,137,116)] uppercase tracking-wider mb-6">Latest Articles</h2>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
                    {regularPosts.map((post, index) => (
                      <BlogCard key={post.id} post={post} index={index + featuredPosts.length} />
                    ))}
                  </div>
                </div>
              )}
            </AnimatePresence>
          )}
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-16 bg-[rgb(132,137,116)]">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-2xl mx-auto text-center"
          >
            <h2 className="text-3xl font-light text-white mb-4">
              Stay Connected
            </h2>
            <p className="text-white/80 mb-8">
              Follow us on social media for the latest insights on Chinese medicine and women's health.
            </p>
            <div className="flex justify-center gap-4">
              <a
                href="https://www.instagram.com/raintreechinesemedicine/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 bg-white text-[rgb(132,137,116)] rounded-full font-medium hover:bg-[#DBB981] transition-colors duration-300"
              >
                Follow on Instagram
              </a>
              <a
                href="https://www.facebook.com/Raintreechinesemedicine"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 bg-transparent text-white border border-white rounded-full font-medium hover:bg-white hover:text-[rgb(132,137,116)] transition-colors duration-300"
              >
                Follow on Facebook
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  )
}
